import { Injectable, OnDestroy } from "@angular/core";
import { UntypedFormBuilder, UntypedFormControl, Validators } from "@angular/forms";
import { select, Store } from "@ngrx/store";
import { CommonState } from "@obg/ngx.common";
import {
	AbstractTranslatableSnackBarService,
	CoreUiInteractionEvent,
	CUSTOMER_MANAGEMENT_MESSAGE_CONTEXT,
	CustomerManagementAction,
	Jurisdiction,
	Message,
	MessageService,
	MessageType,
	SessionSelector,
} from "@obg/ngx.core";
import { TranslationGroup, TranslationService } from "@obg/ngx.core.content";
import { TelemetryService } from "@obg/ngx.global.evo";
import { BehaviorSubject, merge, Observable, Subscription } from "rxjs";
import { filter, map, take, tap } from "rxjs/operators";

import { UpdatedTermsAndConditionsDeclineFormGroup } from "./updated-terms-and-conditions-decline-form.model";

export const UPDATED_TC_ALERT_ID = "UPDATED_TC_ALERT";

@Injectable({
	providedIn: "root",
})
export class UpdatedTermsAndConditionsService implements OnDestroy {
	readonly declineFormGroup: UpdatedTermsAndConditionsDeclineFormGroup;
	readonly isBusy$ = new BehaviorSubject(false);
	readonly isDeclineWarningState$ = new BehaviorSubject(false);
	readonly errorMessage$ = new BehaviorSubject("");
	private data$$: Subscription | undefined;
	private declineTermsAndConditions$$: Subscription | undefined;

	constructor(
		private translationService: TranslationService,
		private telemetryService: TelemetryService,
		private snackBarService: AbstractTranslatableSnackBarService,
		private formBuilder: UntypedFormBuilder,
		private messageService: MessageService,
		private store: Store<CommonState>,
		private sessionSelector: SessionSelector,
		private customerManagementAction: CustomerManagementAction
	) {
		this.declineFormGroup = this.initDeclineFormGroup();
	}

	initialize() {
		if (this.data$$) {
			this.resetFormState();
			this.isBusy$.next(false);
			return;
		}

		const error$ = this.acceptTermsAndConditionsFailed$().pipe(
			filter((errorMessage) => !!errorMessage?.length),
			take(1),
			tap((errorMessage) => {
				this.errorMessage$.next(errorMessage[0].messageCode);
				this.isBusy$.next(false);
			})
		);

		this.data$$ = merge(error$).subscribe();
	}

	ngOnDestroy() {
		this.data$$?.unsubscribe();
		this.declineTermsAndConditions$$?.unsubscribe();
	}

	getTitle(): string {
		return this.translationService.getByKey("terms-and-conditions.updated-title");
	}

	getMessage(jurisdiction: Jurisdiction): string {
		let updatedContentKey = "terms-and-conditions.updated-content";
		if (jurisdiction && jurisdiction === Jurisdiction.Sga) {
			updatedContentKey = `sga-${updatedContentKey}`;
		}
		return this.translationService.getByKey(updatedContentKey);
	}

	getSummary(jurisdiction: Jurisdiction): string | undefined {
		let summaryKey = "terms-and-conditions.summary";
		if (jurisdiction && jurisdiction === Jurisdiction.Sga) {
			summaryKey = `sga-${summaryKey}`;
		}
		const summary = this.translationService.getByKey(summaryKey);
		if (summary !== summaryKey && summary) {
			return summary;
		}
		return undefined;
	}

	acceptTermsAndConditions(): Subscription {
		this.telemetryService.track({
			event: CoreUiInteractionEvent.Submit,
		});

		this.errorMessage$.next("");
		this.isBusy$.next(true);

		return this.acceptTermsAndConditions$().pipe(take(1)).subscribe();
	}

	declineTermsAndConditions() {
		this.errorMessage$.next("");

		this.declineFormGroup.controls.logoutWarning.markAsTouched();
		this.declineFormGroup.controls.closeAccountWarning.markAsTouched();
		if (!this.declineFormGroup.valid) {
			const errorMessage = this.translationService.getByKey("terms-and-conditions.required-fields");
			this.errorMessage$.next(errorMessage);
			return;
		}

		this.isBusy$.next(true);

		this.declineTermsAndConditions$$ = this.declineTermsAndConditions$().pipe(take(1)).subscribe();
	}

	declinedTermsAndConditionsSnackbar() {
		this.snackBarService
			.openSnackBar$(
				{
					message: "terms-and-conditions.decline-success-message",
					action: "general.close",
					duration: 4000,
					type: MessageType.Success,
				},
				[TranslationGroup.General]
			)
			.pipe(take(1))
			.subscribe(); // eslint-disable-line rxjs/no-ignored-subscription
	}

	resetFormState() {
		this.errorMessage$.next("");
		this.declineFormGroup.controls.closeAccountWarning.setValue(false);
		this.declineFormGroup.controls.logoutWarning.setValue(false);
	}

	setDeclineWarningState(isDeclineWarningState: boolean) {
		this.isDeclineWarningState$.next(isDeclineWarningState);
	}

	setBusyState(isBusyState: boolean) {
		this.isBusy$.next(isBusyState);
	}

	get getIsDeclineWarningState$(): Observable<boolean> {
		return this.isDeclineWarningState$.asObservable();
	}

	// TODO: remove the terms and conditions duplications when we move this from Angular
	declineTermsAndConditions$(): Observable<void> {
		return this.store.pipe(
			select(this.sessionSelector.getTermsAndConditionsToken()),
			filter((token) => !!token),
			tap((token) => this.store.dispatch(this.customerManagementAction.declineTermsAndConditions(token!))),
			map(() => undefined)
		);
	}

	private initDeclineFormGroup(): UpdatedTermsAndConditionsDeclineFormGroup {
		return this.formBuilder.group({
			logoutWarning: new UntypedFormControl(false, Validators.required),
			closeAccountWarning: new UntypedFormControl(false, Validators.required),
		}) as UpdatedTermsAndConditionsDeclineFormGroup;
	}

	private acceptTermsAndConditionsFailed$(): Observable<Message[]> {
		return this.messageService.consume$({
			context: CUSTOMER_MANAGEMENT_MESSAGE_CONTEXT.termsAndConditions,
			messageType: MessageType.Error,
		});
	}

	private acceptTermsAndConditions$(): Observable<void> {
		return this.store.pipe(
			select(this.sessionSelector.getTermsAndConditionsToken()),
			filter((token) => !!token),
			tap((token) => this.store.dispatch(this.customerManagementAction.acceptTermsAndConditions(token!))),
			map(() => undefined)
		);
	}
}
